import React, { useState } from 'react';
import {
  lessonApi,
  lessonError,
  partName,
  requestId,
  card,
  button,
  secondary,
  field,
  say,
} from './shared';
import { SlideViewer } from './SlideViewer';
import { LessonSpeaking } from './LessonSpeaking';
import { LessonResult } from './LessonResult';
import { LessonVocabulary } from './LessonVocabulary';
import { LessonQuiz } from './LessonQuiz';
import { CheckCircle2, Star } from 'lucide-react';
import { StudentReviewBanner } from '../classes/StudentReviewBanner';
const parts = ['slides', 'vocabulary', 'speaking', 'writing', 'questions'];
const tabs = ['slides', 'vocabulary', 'speaking', 'writing'];
export function LessonPlayer({ data: initial, classId, lang, onBack, api = lessonApi, studentId, reviewStudent, onRewards, backLabel }) {
  const [data, setData] = useState(initial),
    [part, setPart] = useState('slides'),
    [answers, setAnswers] = useState({}),
    [writing, setWriting] = useState(''),
    [recording, setRecording] = useState(null),
    [micBusy, setMicBusy] = useState(false),
    [viewed, setViewed] = useState(false),
    [busy, setBusy] = useState(false),
    [retrying, setRetrying] = useState(false),
    [vocabularyPractice, setVocabularyPractice] = useState(false),
    [error, setError] = useState(''),
    [celebrate, setCelebrate] = useState(false);
  const pending = React.useRef(null),
    lock = React.useRef(false);
  const stepPanel = React.useRef(null), activityHeading = React.useRef(null), previousPart = React.useRef(part);
  React.useEffect(() => {
    if (previousPart.current !== part && ['slides', 'questions'].includes(part)) {
      activityHeading.current?.focus({ preventScroll: true });
      stepPanel.current?.scrollIntoView?.({ block: 'start' });
    }
    previousPart.current = part;
  }, [part]);
  const lesson = data.lesson;
  const slidesDone = data.parts.some(p => p.part === 'slides' && p.attempts.length);
  const checkDone = data.parts.some(p => p.part === 'questions' && p.attempts.length);
  const slideActivity = part === 'slides' || part === 'questions';
  const reviewing = Boolean(studentId && data.readOnly);
  const base = `/classes/${classId}/lessons/${lesson.id}`,
    query = studentId ? `?studentId=${encodeURIComponent(studentId)}` : '';
  const attempts = data.parts.find((p) => p.part === part)?.attempts || [],
    max = ['slides', 'vocabulary', 'questions'].includes(part) ? 1 : 3,
    locked = data.readOnly || attempts.length >= max;
  const showForm = !attempts.length || retrying;
  const questions = ['vocabulary', 'questions'].includes(part) ? lesson[part] : [];
  const words = lesson.vocabularyWords || [];
  const learning = part === 'vocabulary' && words.length > 0 && !vocabularyPractice;
  const canSubmit =
    part === 'slides'
      ? viewed
      : part === 'writing'
        ? writing.trim().length > 0
        : part === 'speaking'
          ? !!recording
          : questions.every((q) => answers[q.id]);
  const submit = async () => {
    if (lock.current || (!pending.current && (locked || !canSubmit))) return;
    lock.current = true;
    setBusy(true);
    setError('');
    const input =
      part === 'writing'
        ? { text: writing }
        : part === 'speaking'
          ? recording
          : part === 'slides'
            ? {}
            : { answers: questions.map((q) => ({ questionId: q.id, optionId: answers[q.id] })) };
    // Keep the same payload and request ID after uncertain network failure.
    pending.current ||= { part, body: { ...input, requestId: requestId(), revision: data.revision } };
    try {
      const result = await api(`${base}/parts/${pending.current.part}`, pending.current.body);
      onRewards?.(result.lessonRewardStars);
      const savedPart = pending.current.part;
      pending.current = null;
      // Celebrate a new full completion, not a retry or reopening saved work.
      if (!data.readOnly && !data.parts.some(p => p.part === savedPart && p.attempts.length)
        && parts.every(p => p === savedPart || data.parts.some(row => row.part === p && row.attempts.length))) {
        setCelebrate(true);
      }
      setData((d) => ({
        ...d,
        parts: [
          ...d.parts.filter((p) => p.part !== savedPart),
          {
            part: savedPart,
            attempts: [...(d.parts.find((p) => p.part === savedPart)?.attempts || []), result.attempt],
          },
        ],
      }));
      setAnswers({});
      setRecording(null);
      setWriting('');
      setRetrying(false);
      if (savedPart === 'slides' && lesson.questions.length) setPart('questions');
    } catch (e) {
      if (e.status >= 400 && e.status < 500) pending.current = null;
      setError(lessonError(e, lang));
    } finally {
      lock.current = false;
      setBusy(false);
    }
  };
  const completedCount = parts.filter(p => data.parts.some(row => row.part === p && row.attempts.length)).length;
  const completed = completedCount === parts.length;
  return (
    <div className="space-y-6" data-testid="lesson-player">
      <button
        className={secondary + ' max-w-full break-words text-left'}
        disabled={busy || micBusy}
        onClick={() => {
          if (
            (writing.trim() || Object.keys(answers).length || recording || pending.current) &&
            !window.confirm(
              say(
                lang,
                'Leave this activity? Unsubmitted changes will not be saved.',
                '离开本项练习？尚未提交的内容不会保存。',
              ),
            )
          )
            return;
          onBack();
        }}
      >
        {backLabel || say(lang, '← Back to class', '← 返回班级')}
      </button>
      {reviewing && <StudentReviewBanner name={reviewStudent?.name} className={reviewStudent?.className} lang={lang} />}
      <header>
        <p className="text-indigo-600 font-bold">{say(lang, `Lesson ${lesson.number}`, `第 ${lesson.number} 课`)}</p>
        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-2">{say(lang, lesson.title, lesson.titleZh)}</h2>
        <p className="text-slate-500 mt-3">
          {reviewing ? say(lang, 'Student’s lesson work — view submitted activities and feedback below.', '学生的课程作业：在下方查看已提交的作答和反馈。') : completed
            ? say(
                lang,
                'All activities submitted. You can review your work below.',
                '所有学习任务已提交，可以在下方复习。',
              )
            : say(lang, 'Review the slides and try each short activity.', '先复习课件，再完成各项小练习。')}
        </p>
        <div className="mt-5 space-y-2">
          <p className="font-semibold text-sm text-slate-600" role="status">{say(lang, `${completedCount} of 5 activities completed`, `已完成 ${completedCount} / 5 项学习任务`)}</p>
          <div role="progressbar" aria-label={say(lang, 'Lesson progress', '课程进度')} aria-valuemin={0} aria-valuemax={5} aria-valuenow={completedCount} className="h-2 rounded-full bg-slate-200 overflow-hidden">
            <div className="lesson-progress-fill h-full rounded-full bg-blue-500" style={{ width: `${completedCount * 20}%` }} />
          </div>
        </div>
        {data.readOnly && !reviewing && (
          <p className="mt-3 font-bold text-amber-800">
            {data.isOwner
              ? say(lang, 'Teacher preview', '教师预览')
              : say(lang, 'Earlier course — saved work is read-only.', '往期课程，已保存的作业仅供查看。')}
          </p>
        )}
      </header>
      {completed && !data.readOnly && <section aria-label={say(lang, 'Lesson complete', '课程已完成')} aria-live={celebrate ? 'polite' : 'off'} className={`lesson-celebration ${celebrate ? 'lesson-celebration--new' : ''}`}>
        <span className="completion-star" aria-hidden="true"><Star size={28} fill="currentColor" /></span>
        <div className="min-w-0"><h3 className="font-semibold text-lg text-slate-900">{say(lang, 'Nicely done — lesson complete!', '做得好，这一课完成啦！')}</h3>
        <p className="mt-1 text-sm leading-relaxed text-slate-600">{say(lang, 'All five activities are saved. Take a moment to enjoy your progress.', '五项学习任务都已保存。为自己的进步点个赞吧。')}</p></div>
      </section>}
      <nav aria-label={say(lang, 'Lesson activities', '学习任务')} className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
        {tabs.map((p) => {
          const done = p === 'slides' ? slidesDone && checkDone : data.parts.some(r => r.part === p && r.attempts.length);
          const selected = p === 'slides' ? slideActivity : part === p;
          const started = p === 'slides' && (slidesDone || checkDone);
          return (
          <button
            key={p}
            aria-pressed={selected}
            disabled={busy || micBusy || !!pending.current}
            className={`min-w-0 min-h-[76px] border rounded-xl px-3 sm:px-4 py-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700 disabled:opacity-50 ${selected ? 'border-blue-600 ring-1 ring-blue-600 bg-blue-50 text-blue-950' : done ? 'border-blue-400 bg-white text-slate-600' : 'border-slate-200 bg-white text-slate-600'}`}
            onClick={() => {
              if (selected) return;
              if (
                (writing.trim() || Object.keys(answers).length || recording) &&
                !window.confirm(
                  say(
                    lang,
                    'Switch activities? Unsubmitted changes will not be saved.',
                    '切换练习？尚未提交的内容不会保存。',
                  ),
                )
              )
                return;
              setPart(p);
              setAnswers({});
              setWriting('');
              setRecording(null);
              setError('');
              setRetrying(false);
              setVocabularyPractice(false);
            }}
          >
            <span className="block font-semibold text-sm">{p === 'slides' ? say(lang, 'Slides & Quick check', '课件与小测验') : partName(p, lang)}</span>
            <span className="mt-1 flex items-center gap-1.5 text-xs sm:text-sm">{done && <CheckCircle2 className="lesson-tab-check text-blue-600" size={14} aria-hidden="true" />}{done ? say(lang, 'Completed', '已完成') : started ? say(lang, '1 of 2 completed', '已完成 1 / 2 步') : say(lang, 'Not started', '未完成')}</span>
          </button>
        ); })}
      </nav>
      <section className={card + ' space-y-6'}>
        {slideActivity && <div ref={stepPanel} className="space-y-3 scroll-mt-24">
          <div role="group" aria-label={say(lang, 'Slides and quick check steps', '课件与小测验步骤')} className="flex flex-col sm:flex-row gap-2">
            {['slides', 'questions'].map((step, index) => <button key={step} type="button" aria-pressed={part === step}
              disabled={busy || !!pending.current || (step === 'questions' && !slidesDone && !checkDone && !data.readOnly)}
              onClick={() => { setPart(step); setError(''); setRetrying(false); }}
              className={`${secondary} flex items-center justify-center gap-2 sm:flex-1 ${part === step ? 'ring-1 ring-blue-400' : ''}`}>
              {index + 1}. {partName(step, lang)}{(step === 'slides' ? slidesDone : checkDone) && <CheckCircle2 size={16} className="text-blue-600" aria-label={say(lang, 'Completed', '已完成')} />}
            </button>)}
          </div>
          <p className="text-sm text-slate-500">{say(lang, 'Review the slides, then test what you learned. Each step earns 3 completion stars once.', '先复习课件，再通过小测验检查所学内容。每一步完成后可获得一次 3 颗星星奖励。')}</p>
        </div>}
        <h3 ref={activityHeading} tabIndex={-1} className="text-xl font-semibold tracking-tight outline-none">{partName(part, lang)}</h3>
        {reviewing && !attempts.length && <p className="rounded-xl bg-teal-50 p-4 text-teal-800">{say(lang, 'No submission from this student yet.', '这位学生尚未提交本项作业。')}</p>}
        {error && (
          <p role="alert" className="text-rose-700">
            {error}
          </p>
        )}
        <div hidden={part !== 'slides'} className="space-y-6">
            <SlideViewer
              visible={part === 'slides'}
              slides={lesson.slides}
              basePath={base}
              query={query}
              lang={lang}
              onViewedAll={() => setViewed(true)}
            />
            {!data.readOnly && <p className="text-sm text-slate-500">
              {say(
                lang,
                'View every slide, then continue to Quick check. Your slide review is saved before the questions begin.',
                '看完每页课件后，继续小测验。开始答题前会先保存课件复习记录。',
              )}
            </p>}
        </div>
        {retrying && <div className="rounded-xl bg-indigo-50 p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-indigo-900 font-semibold">{say(lang, 'New attempt — your earlier work is saved.', '再次尝试：以前的作答已保存。')}</p>
          <button className={secondary} disabled={busy || micBusy || !!pending.current} onClick={() => {
            if ((writing.trim() || recording) && !window.confirm(say(lang, 'Discard this unsubmitted attempt?', '放弃这次尚未提交的作答？'))) return;
            setRetrying(false); setWriting(''); setRecording(null); setError('');
          }}>{say(lang, 'Back to saved result', '返回已保存的结果')}</button>
        </div>}
        {showForm && learning && <LessonVocabulary words={words} lang={lang} onStart={() => setVocabularyPractice(true)} />}
        {showForm && !!questions.length && !learning && <LessonQuiz key={part} questions={questions} answers={answers} onChange={setAnswers}
          lang={lang} disabled={locked} busy={busy} pending={!!pending.current} readOnly={data.readOnly} onSubmit={submit}
          onLearn={part === 'vocabulary' && words.length ? () => setVocabularyPractice(false) : undefined} />}
        {showForm && part === 'writing' && (
          <>
            <p className="text-lg leading-relaxed">{say(lang, lesson.writing.prompt, lesson.writing.promptZh)}</p>
            <div className="bg-indigo-50 rounded-xl p-4 space-y-2">
              {lesson.writing.starters.map((s) => (
                <p key={s}>{s}</p>
              ))}
            </div>
            {!data.readOnly && <label className="block font-bold">
              {say(lang, 'Your writing', '你的作文')}
              <textarea
                className={field + ' mt-2 min-h-44'}
                maxLength={2000}
                disabled={locked || busy || !!pending.current}
                value={writing}
                onChange={(e) => setWriting(e.target.value)}
              />
            </label>}
            {!data.readOnly && <p className="text-sm text-slate-500">
              {say(
                lang,
                'Submit for an AI score out of 5, feedback and a suggestion. Your teacher can also read your writing. Completion earns 3 stars once, separate from your score.',
                '提交后可获得 5 分制 AI 评分、反馈和建议，老师也可以查看作文。完成任务可获得一次 3 颗星星奖励，与评分分开计算。',
              )}
            </p>}
          </>
        )}
        {showForm && part === 'speaking' && reviewing && <div className="rounded-xl border border-teal-100 p-5 space-y-3">
          <p className="text-sm font-semibold text-teal-800">{say(lang, 'Assigned speaking sentence', '布置的口语句子')}</p>
          <p className="text-xl font-semibold">{lesson.speaking.sentence}</p>
          {lang === 'zh' && lesson.speaking.hintZh && <p className="text-slate-600">{lesson.speaking.hintZh}</p>}
        </div>}
        {showForm && part === 'speaking' && !reviewing && (
          <LessonSpeaking
            key={`${lesson.id}-${attempts.length}`}
            sentence={lesson.speaking.sentence}
            hintZh={lesson.speaking.hintZh}
            lang={lang}
            disabled={locked || busy || !!pending.current}
            onRecording={setRecording}
            onStatus={setMicBusy}
          />
        )}
        {!data.readOnly && showForm && !questions.length && (
          <button
            className={button + ' w-full sm:w-auto'}
            disabled={locked || busy || micBusy || (!canSubmit && !pending.current)}
            onClick={submit}
          >
            {busy
              ? (part === 'writing' ? say(lang, 'Getting AI feedback and saving…', '正在获取 AI 反馈并保存…') : say(lang, 'Saving…', '正在保存…'))
              : pending.current
                ? say(lang, 'Retry saving this answer', '重试保存这份答案')
                : part === 'slides'
                  ? say(lang, 'Continue to Quick check', '继续小测验')
                  : say(lang, 'Submit this activity', '提交本项练习')}
          </button>
        )}
        {showForm && !data.readOnly && ['speaking', 'writing'].includes(part) && (
          <p className="text-sm text-slate-500">
            {say(
              lang,
              max === 1 ? 'One submission only. Check your answers before submitting.' : `${Math.max(0, max - attempts.length)} attempts remaining. Your answer is saved only when you submit.`,
              max === 1 ? '只能提交一次，请检查答案后再提交。' : `还可以尝试 ${Math.max(0, max - attempts.length)} 次。点击提交后才会保存答案。`,
            )}
          </p>
        )}
        {!!attempts.length && !retrying && <LessonResult attempts={attempts} part={part} lesson={lesson} base={base} query={query} lang={lang}
          reviewing={reviewing} remaining={Math.max(0, max - attempts.length)} readOnly={data.readOnly} onRetry={() => { setRetrying(true); setError(''); }} />}
        {part === 'slides' && (slidesDone || data.readOnly) && <button type="button" className={button + ' w-full sm:w-auto'} disabled={busy || !!pending.current}
          onClick={() => { setPart('questions'); setError(''); }}>{checkDone ? say(lang, 'View Quick check result', '查看小测验结果') : say(lang, 'Continue to Quick check', '继续小测验')}</button>}
        {part === 'vocabulary' && !!attempts.length && !!words.length && <details className="rounded-xl border border-slate-200 p-4 sm:p-5">
          <summary className="font-bold cursor-pointer py-3">{say(lang, 'Review word cards', '复习单词卡片')}</summary>
          <div className="mt-5"><LessonVocabulary words={words} lang={lang} /></div>
        </details>}
      </section>
    </div>
  );
}
